import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { FolderOpen, Zap } from 'lucide-react';
import '../../styles-wa/templates_view.css';
import { getWhatsAppTemplates, getTemplates, createCampaign, launchCampaignNew, getContacts, getApiErrorMessage, approveTemplate, rejectTemplate, syncWhatsAppTemplates, deleteWhatsAppTemplate } from '../../api-wa';
import Toast from '../../Components/WhatsApp/Toast';
import { useConfig } from '../../Context/WaConfigContext';
import TemplateActionMenu from '../../Components/WhatsApp/TemplateActionMenu';
import TemplatePreviewModal from '../../Components/WhatsApp/TemplatePreviewModal';
import ContactSelectionModal from '../../Components/WhatsApp/ContactSelectionModal';
import { ArchiveConfirmModal, DeleteConfirmModal } from '../../Components/WhatsApp/ConfirmationModals';

const TemplatesView = ({ onCreateClick, onLaunch, searchQuery = '', setSearchQuery }) => {
    const { activeConfigId } = useConfig();
    const [templates, setTemplates] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [toast, setToast] = useState(null);
    const [activeTab, setActiveTab] = useState('All');
    const [activeStatus, setActiveStatus] = useState('All');
    const [categoryOpen, setCategoryOpen] = useState(false);
    const [statusOpen, setStatusOpen] = useState(false);
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
                setCategoryOpen(false);
                setStatusOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, []);

    // --- Modal State ---
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [currentStep, setCurrentStep] = useState(1);
    const [audienceType, setAudienceType] = useState('');
    const [selectedCourses, setSelectedCourses] = useState([]);
    const [isCourseDropdownOpen, setIsCourseDropdownOpen] = useState(false);
    const [selectedContacts, setSelectedContacts] = useState(new Set());
    const [campaignName, setCampaignName] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);

    // --- NEW: Modern UI Modal States ---
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [previewTemplate, setPreviewTemplate] = useState(null);
    const [isContactSelectionOpen, setIsContactSelectionOpen] = useState(false);
    const [isArchiveConfirmOpen, setIsArchiveConfirmOpen] = useState(false);
    const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
    const [templateToArchive, setTemplateToArchive] = useState(null);
    const [templateToDelete, setTemplateToDelete] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editingTemplate, setEditingTemplate] = useState(null);
    const [isApprovingTemplate, setIsApprovingTemplate] = useState(false);
    const [isSyncing, setIsSyncing] = useState(false);
    const [isFetchingContacts, setIsFetchingContacts] = useState(false);


    // --- State Switching Helper ---
    const handleAudienceToggle = (type) => {
        if (type === audienceType) return;
        setAudienceType(type);
        setSelectedCourses([]);
        setSelectedContacts(new Set());
    };

    // --- Fetch Templates & Contacts from Backend ---
    const [allContacts, setAllContacts] = useState([]);

    useEffect(() => {
        if (!activeConfigId) {
            setLoading(false);
            setTemplates([]);
            return;
        }
        const loadData = async () => {
            try {
                setLoading(true);
                const templatesRes = await getTemplates();

                setTemplates(templatesRes);
                setError(null);
            } catch (err) {
                const errorMsg = getApiErrorMessage(err, 'Failed to load templates');
                setError(errorMsg);
                setToast({ type: 'error', message: errorMsg });
            } finally {
                setLoading(false);
            }
        };
        loadData();
    }, [activeConfigId]);

    const handleSync = async () => {
        try {
            setIsSyncing(true);
            const res = await syncWhatsAppTemplates();
            setToast({ type: 'success', message: `Sync complete! ${res.synced} templates updated.` });

            // Reload templates
            const templatesRes = await getTemplates();
            setTemplates(templatesRes);

        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to sync templates');
            setToast({ type: 'error', message: errorMsg });
        } finally {
            setIsSyncing(false);
        }
    };


    // --- Dummy Data (Fallback) ---
    const COURSES = ['Front-End Developer', 'UI/UX Design', 'Data Science', 'Full-Stack BootCamp'];

    const ALL_CONTACTS = allContacts.length > 0 ? allContacts : [
        { id: 101, name: 'Sample Contact', phone: '+1234567890', company: 'Sample Co' },
    ];

    // --- Modal Logic ---
    const openSendModal = (template) => {
        setSelectedTemplate(template);
        setIsModalOpen(true);
        setCurrentStep(1);
        setSelectedContacts(new Set());
        setShowSuccess(false);
    };

    const closeSendModal = () => {
        setIsModalOpen(false);
        setSelectedTemplate(null);
        setCurrentStep(1);
        setAudienceType('');
        setSelectedCourses([]);
        setIsCourseDropdownOpen(false);
        setSelectedContacts(new Set());
        setCampaignName('');
        setShowSuccess(false);
    };

    const handleSend = async () => {
        try {
            const contactSource = ALL_CONTACTS;
            const selectedContactIds = Array.from(selectedContacts);
            const selectedContactObjects = contactSource.filter(c => selectedContacts.has(c.id));

            const contactsPhones = selectedContactObjects.map(c => c.phone || c.phone_number).filter(Boolean);

            const payload = {
                id: null,
                name: campaignName.trim() || `Template Send - ${new Date().toLocaleString()}`,
                template_id: Number(selectedTemplate.id),
                contacts: contactsPhones,
                variables: {},
                scheduled_at: null
            };

            await launchCampaignNew(payload);

            const contactsSummary = selectedContactObjects.map(c => ({
                name: c.name,
                company: c.company || 'Contact',
                phone: c.phone || c.phone_number,
                status: 'Delivered'
            }));

            if (onLaunch) onLaunch(
                selectedContacts.size,
                audienceType,
                campaignName,
                selectedTemplate?.name,
                selectedTemplate?.body,
                contactsSummary
            );

            setToast({ type: 'success', message: 'Campaign created successfully!' });
            setShowSuccess(true);
            setTimeout(() => {
                closeSendModal();
            }, 2200);
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to create campaign');
            setToast({ type: 'error', message: errorMsg });
        }
    };

    const toggleContact = (id) => {
        const newSelected = new Set(selectedContacts);
        if (newSelected.has(id)) {
            newSelected.delete(id);
        } else {
            newSelected.add(id);
        }
        setSelectedContacts(newSelected);
    };

    const toggleSelectAll = () => {
        const contactSource = ALL_CONTACTS;
        if (selectedContacts.size === contactSource.length) {
            // Deselect all
            setSelectedContacts(new Set());
        } else {
            // Select all
            const allIds = new Set(contactSource.map(c => c.id));
            setSelectedContacts(allIds);
        }
    };

    // --- NEW: Modern UI Handlers ---
    const handleOpenPreview = (template) => {
        setPreviewTemplate(template);
        setIsPreviewModalOpen(true);
    };

    const handleClosePreview = () => {
        setIsPreviewModalOpen(false);
        setPreviewTemplate(null);
    };

    const handleStartSending = async () => {
        setIsPreviewModalOpen(false);
        setSelectedContacts(new Set());
        
        if (allContacts.length === 0) {
            try {
                setIsFetchingContacts(true);
                const contactsRes = await getContacts();
                setAllContacts(contactsRes);
            } catch (err) {
                const errorMsg = getApiErrorMessage(err, 'Failed to load contacts');
                setToast({ type: 'error', message: errorMsg });
                setIsFetchingContacts(false);
                return; // don't open modal if failed
            } finally {
                setIsFetchingContacts(false);
            }
        }
        
        setIsContactSelectionOpen(true);
    };

    const handleCloseContactSelection = () => {
        setIsContactSelectionOpen(false);
        setSelectedContacts(new Set());
    };

    const handleArchiveTemplate = (template) => {
        setTemplateToArchive(template);
        setIsArchiveConfirmOpen(true);
    };

    const handleConfirmArchive = async () => {
        if (!templateToArchive) return;
        try {
            setIsProcessing(true);
            // Call archive API (you'll need to add this to your API)
            // For now, update status to ARCHIVED locally
            const updatedTemplates = templates.map(t =>
                t.id === templateToArchive.id
                    ? { ...t, status: 'ARCHIVED' }
                    : t
            );
            setTemplates(updatedTemplates);
            setToast({ type: 'success', message: `${templateToArchive.name} archived successfully!` });
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to archive template');
            setToast({ type: 'error', message: errorMsg });
        } finally {
            setIsProcessing(false);
            setIsArchiveConfirmOpen(false);
            setTemplateToArchive(null);
        }
    };

    const handleDeleteTemplate = (template) => {
        setTemplateToDelete(template);
        setIsDeleteConfirmOpen(true);
    };

    const handleConfirmDelete = async () => {
        if (!templateToDelete) return;
        try {
            setIsProcessing(true);
            await deleteWhatsAppTemplate(templateToDelete.id);
            const updatedTemplates = templates.filter(t => t.id !== templateToDelete.id);
            setTemplates(updatedTemplates);
            setToast({ type: 'success', message: `${templateToDelete.name} deleted from CRM and Meta.` });
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to delete template');
            setToast({ type: 'error', message: errorMsg });
        } finally {
            setIsProcessing(false);
            setIsDeleteConfirmOpen(false);
            setTemplateToDelete(null);
        }
    };

    // --- Edit Modal Logic ---
    const openEditModal = (template, e) => {
        e.stopPropagation();
        setEditingTemplate(template);
        setIsEditModalOpen(true);
    };

    const closeEditModal = () => {
        setIsEditModalOpen(false);
        setEditingTemplate(null);
        setIsApprovingTemplate(false);
    };

    const handleApproveTemplate = async () => {
        if (!editingTemplate) return;
        try {
            setIsApprovingTemplate(true);
            const updatedTemplate = await approveTemplate(editingTemplate.id);

            // Update templates list
            setTemplates(templates.map(t => t.id === editingTemplate.id ? updatedTemplate : t));

            setToast({ type: 'success', message: 'Template approved successfully!' });
            setTimeout(() => {
                closeEditModal();
            }, 1500);
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to approve template');
            setToast({ type: 'error', message: errorMsg });
        } finally {
            setIsApprovingTemplate(false);
        }
    };

    const handleRejectTemplate = async () => {
        if (!editingTemplate) return;
        try {
            setIsApprovingTemplate(true);
            const updatedTemplate = await rejectTemplate(editingTemplate.id);

            // Update templates list
            setTemplates(templates.map(t => t.id === editingTemplate.id ? updatedTemplate : t));

            setToast({ type: 'success', message: 'Template rejected successfully!' });
            setTimeout(() => {
                closeEditModal();
            }, 1500);
        } catch (err) {
            const errorMsg = getApiErrorMessage(err, 'Failed to reject template');
            setToast({ type: 'error', message: errorMsg });
        } finally {
            setIsApprovingTemplate(false);
        }
    };

    // Helper functions for styling based on data
    const getStatusClass = (status) => {
        switch (status) {
            case 'APPROVED': return 'status-approved';
            case 'PENDING': return 'status-pending';
            case 'REJECTED': return 'status-rejected';
            default: return '';
        }
    };

    const getCategoryClass = (category) => {
        switch (category) {
            case 'MARKETING': return 'cat-marketing';
            case 'UTILITY': return 'cat-utility';
            case 'AUTHENTICATION': return 'cat-auth';
            default: return '';
        }
    };

    // Unified Filter logic for Category AND Status AND Search Query
    const filteredTemplates = templates.filter(t => {
        const matchesCategory = activeTab === 'All' || t.category === activeTab.toUpperCase();
        const matchesStatus = activeStatus === 'All' || t.status === activeStatus.toUpperCase();
        const matchesSearch = !searchQuery || t.name.toLowerCase().includes(searchQuery.toLowerCase());
        return matchesCategory && matchesStatus && matchesSearch;
    });

    const getTemplateWeight = (t) => {
        let weight = 0;
        const headerType = (t.header_type || 'NONE').toUpperCase();
        if (headerType === 'IMAGE' || headerType === 'VIDEO') {
            weight += 200;
        } else if (headerType === 'DOCUMENT') {
            weight += 100;
        }
        if (t.header_text) weight += t.header_text.length;
        if (t.body) weight += t.body.length;
        if (t.footer_text) weight += t.footer_text.length;
        if (t.buttons && Array.isArray(t.buttons)) {
            weight += t.buttons.length * 50;
        }
        return weight;
    };

    const sortedTemplates = [...filteredTemplates].sort((a, b) => getTemplateWeight(a) - getTemplateWeight(b));

    // WhatsApp text formatting: *bold*, _italic_, ~strikethrough~
    const formatWhatsAppText = (text) => {
        if (!text) return '';
        // Split into parts, preserving formatting markers
        const parts = [];
        let remaining = text;
        const regex = /(\*[^\s*\n](?:[^*]*?[^\s*\n])?\*|_[^\s_\n](?:[^_]*?[^\s_\n])?_|~[^\s~\n](?:[^~]*?[^\s~\n])?~)/g;
        let lastIndex = 0;
        let match;
        while ((match = regex.exec(remaining)) !== null) {
            if (match.index > lastIndex) {
                parts.push(remaining.substring(lastIndex, match.index));
            }
            const raw = match[0];
            if (raw.startsWith('*') && raw.endsWith('*')) {
                parts.push(<strong key={match.index}>{raw.slice(1, -1)}</strong>);
            } else if (raw.startsWith('_') && raw.endsWith('_')) {
                parts.push(<em key={match.index}>{raw.slice(1, -1)}</em>);
            } else if (raw.startsWith('~') && raw.endsWith('~')) {
                parts.push(<s key={match.index}>{raw.slice(1, -1)}</s>);
            }
            lastIndex = match.index + raw.length;
        }
        if (lastIndex < remaining.length) {
            parts.push(remaining.substring(lastIndex));
        }
        return parts.length > 0 ? parts : text;
    };

    // Helper to parse {{variable}} and style them green
    const renderParsedBody = (text) => {
        if (!text) return null;
        const parts = text.split(/(\{\{[^}]+\}\})/g);
        return parts.map((part, index) => {
            if (part.startsWith('{{') && part.endsWith('}}')) {
                return <span key={index} className="template-variable">{part}</span>;
            }
            return <React.Fragment key={index}>{formatWhatsAppText(part)}</React.Fragment>;
        });
    };
    const renderWhatsAppPreview = (template) => {
        if (!template) return null;
        
        const headerType = (template.header_type || 'NONE').toUpperCase();
        const autoMediaUrl = 
            template.uploaded_preview_image ||
            template.meta_media_url ||
            template.header_media ||
            (template.sample_values && (template.sample_values.header_handle || template.sample_values.media_url)) ||
            '';
        
        return (
            <div className="whatsapp-preview-container">
                <div className="message-bubble">
                    {/* Header Image */}
                    {headerType === 'IMAGE' && (
                        autoMediaUrl ? (
                            <div style={{
                                width: '100%',
                                height: '130px',
                                borderRadius: '12px',
                                overflow: 'hidden',
                                marginBottom: '10px',
                                border: '1px solid var(--border-color)'
                            }}>
                                {(autoMediaUrl.startsWith('http') || autoMediaUrl.startsWith('data:') || autoMediaUrl.startsWith('blob:')) ? (
                                    <img 
                                        src={autoMediaUrl} 
                                        alt="Template Header" 
                                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                    />
                                ) : (
                                    <div className="meta-handle-display-box" style={{ padding: '24px', background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.05) 0%, rgba(99, 102, 241, 0.1) 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px', borderRadius: '12px', border: '1px solid rgba(117, 81, 255, 0.2)', width: '100%', height: '100%', justifyContent: 'center', boxSizing: 'border-box' }}>
                                        <span style={{ fontSize: '24px' }}>🖼️</span>
                                        <span style={{ fontSize: '11px', fontWeight: '700', color: 'var(--brand-purple)', textAlign: 'center' }}>Meta Sync Media</span>
                                        <span style={{ fontSize: '9px', color: '#64748b', fontFamily: 'monospace', textAlign: 'center', wordBreak: 'break-all' }}>ID: {autoMediaUrl}</span>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{
                                width: '100%',
                                height: '130px',
                                background: 'linear-gradient(135deg, rgba(117, 81, 255, 0.04) 0%, rgba(99, 102, 241, 0.08) 100%)',
                                borderRadius: '12px',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '8px',
                                color: 'var(--brand-purple)',
                                marginBottom: '10px',
                                border: '1.5px dashed rgba(117, 81, 255, 0.3)',
                                overflow: 'hidden'
                            }}>
                                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.8 }}>
                                    <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                    <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                    <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                                <span style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '1px', opacity: 0.8 }}>Header Image Placeholder</span>
                            </div>
                        )
                    )}

                    {/* Header Video */}
                    {headerType === 'VIDEO' && (
                        <div style={{
                            width: '100%',
                            height: '130px',
                            background: '#e2e8f0',
                            borderRadius: '8px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            color: '#64748b',
                            marginBottom: '10px',
                            border: '1px solid #cbd5e1'
                        }}>
                            <div style={{
                                width: '36px',
                                height: '36px',
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.9)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                            }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style={{ marginLeft: '2px', color: '#475569' }}>
                                    <polygon points="5 3 19 12 5 21 5 3"></polygon>
                                </svg>
                            </div>
                            <span style={{ fontSize: '11px', fontWeight: '800', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Header Video</span>
                        </div>
                    )}

                    {/* Header Document */}
                    {headerType === 'DOCUMENT' && (
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '10px 12px',
                            background: '#f8fafc',
                            border: '1.5px solid #e2e8f0',
                            borderRadius: '10px',
                            marginBottom: '10px'
                        }}>
                            <div style={{
                                background: '#ef4444',
                                padding: '8px',
                                borderRadius: '8px',
                                color: '#fff',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                                    <polyline points="14 2 14 8 20 8"></polyline>
                                    <line x1="16" y1="13" x2="8" y2="13"></line>
                                    <line x1="16" y1="17" x2="8" y2="17"></line>
                                </svg>
                            </div>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1px' }}>
                                <span style={{ fontSize: '12px', fontWeight: '700', color: '#1e293b' }}>document.pdf</span>
                                <span style={{ fontSize: '10px', color: '#64748b' }}>PDF Document</span>
                            </div>
                        </div>
                    )}

                    {/* Header Text */}
                    {headerType === 'TEXT' && template.header_text && (
                        <div style={{
                            fontSize: '14px',
                            fontWeight: '800',
                            color: '#0f172a',
                            marginBottom: '6px',
                            lineHeight: '1.3'
                        }}>
                            {template.header_text}
                        </div>
                    )}

                    {/* Body Content */}
                    <div className="message-body" style={{ fontSize: '13px', color: 'var(--text-dark)' }}>
                        {renderParsedBody(template.body)}
                    </div>

                    {/* Footer Text */}
                    {template.footer_text && (
                        <div style={{
                            fontSize: '11px',
                            color: 'var(--text-gray)',
                            marginTop: '6px',
                            borderTop: '1px dashed var(--border-color)',
                            paddingTop: '6px'
                        }}>
                            {template.footer_text}
                        </div>
                    )}
                </div>

                {/* Template Action Buttons */}
                {template.buttons && Array.isArray(template.buttons) && template.buttons.length > 0 && (
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        background: 'var(--brand-purple-light, rgba(99, 102, 241, 0.03))',
                        border: '1px solid rgba(99, 102, 241, 0.15)',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        marginTop: '10px'
                    }}>
                        {template.buttons.map((btn, idx) => (
                            <div 
                                key={idx}
                                style={{
                                    padding: '10px 12px',
                                    textAlign: 'center',
                                    fontSize: '12.5px',
                                    fontWeight: '700',
                                    color: '#6366f1', // Explicit solid brand-purple color
                                    borderBottom: idx < template.buttons.length - 1 ? '1px solid rgba(99, 102, 241, 0.1)' : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '6px',
                                    background: 'transparent',
                                    cursor: 'pointer'
                                }}
                            >
                                {btn.type === 'PHONE_NUMBER' && (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path></svg>
                                )}
                                {btn.type === 'URL' && (
                                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2.5"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path><polyline points="15 3 21 3 21 9"></polyline><line x1="10" y1="14" x2="21" y2="3"></line></svg>
                                )}
                                <span>{btn.text}</span>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <main className="main-content" style={{ gap: '0px', overflow: 'visible' }}>
            {/* Toast Notification */}
            {toast && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast(null)}
                />
            )}

            {/* Loading State */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '60px 24px' }}>
                    <h3>Loading templates...</h3>
                </div>
            ) : error ? (
                <div style={{ textAlign: 'center', padding: '60px 24px', color: 'var(--text-error)' }}>
                    <h3>Error loading templates</h3>
                    <p>{error}</p>
                </div>
            ) : (
                <>

                    {/* Premium Control Bar */}
                    <div ref={dropdownRef} className="filter-toolbar">
                        
                        {/* Left Side: Premium Dropdowns */}
                        <div className="filter-toolbar-left">
                            {/* Category Dropdown Selector */}
                            <div className="filter-dropdown-wrapper">
                                <button
                                    onClick={() => {
                                        setCategoryOpen(!categoryOpen);
                                        setStatusOpen(false);
                                    }}
                                    className="filter-dropdown-trigger"
                                    aria-haspopup="listbox"
                                    aria-expanded={categoryOpen}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <FolderOpen size={14} style={{ color: '#64748b' }} />
                                        Category: {activeTab}
                                    </span>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.2s', transform: categoryOpen ? 'rotate(180deg)' : 'none' }}>
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </button>
                                
                                {categoryOpen && (
                                    <div className="filter-dropdown-menu" role="listbox">
                                        {['All', 'Marketing', 'Utility', 'Authentication'].map(item => (
                                            <div
                                                key={item}
                                                role="option"
                                                aria-selected={activeTab === item}
                                                onClick={() => {
                                                    setActiveTab(item);
                                                    setCategoryOpen(false);
                                                }}
                                                className={`filter-dropdown-item ${activeTab === item ? 'active' : ''}`}
                                            >
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Status Dropdown Selector */}
                            <div className="filter-dropdown-wrapper">
                                <button
                                    onClick={() => {
                                        setStatusOpen(!statusOpen);
                                        setCategoryOpen(false);
                                    }}
                                    className="filter-dropdown-trigger"
                                    aria-haspopup="listbox"
                                    aria-expanded={statusOpen}
                                >
                                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        <Zap size={14} style={{ color: '#f59e0b' }} />
                                        Status: {activeStatus}
                                    </span>
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" style={{ transition: 'transform 0.2s', transform: statusOpen ? 'rotate(180deg)' : 'none' }}>
                                        <path d="M6 9l6 6 6-6" />
                                    </svg>
                                </button>
                                
                                {statusOpen && (
                                    <div className="filter-dropdown-menu" role="listbox">
                                        {['All', 'Approved', 'Pending', 'Rejected'].map(item => (
                                            <div
                                                key={item}
                                                role="option"
                                                aria-selected={activeStatus === item}
                                                onClick={() => {
                                                    setActiveStatus(item);
                                                    setStatusOpen(false);
                                                }}
                                                className={`filter-dropdown-item ${activeStatus === item ? 'active' : ''}`}
                                            >
                                                {item}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Center Side: Premium search bar */}
                        <div className="filter-toolbar-center">
                            <div className="filter-search-wrapper">
                                <span className="filter-search-icon">
                                    <svg 
                                        width="14" 
                                        height="14" 
                                        viewBox="0 0 24 24" 
                                        fill="none" 
                                        stroke="currentColor" 
                                        strokeWidth="2.5" 
                                    >
                                        <circle cx="11" cy="11" r="8"></circle>
                                        <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                    </svg>
                                </span>
                                <input 
                                    type="text" 
                                    placeholder="Search templates..." 
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery && setSearchQuery(e.target.value)}
                                    className="filter-search-input"
                                />
                                {searchQuery && (
                                    <button
                                        type="button"
                                        onClick={() => setSearchQuery && setSearchQuery('')}
                                        className="filter-search-clear"
                                        title="Clear search"
                                    >
                                        ✕
                                    </button>
                                )}
                            </div>
                        </div>

                        {/* Right Side: Simple Actions */}
                        <div className="filter-toolbar-right">
                            <button
                                onClick={handleSync}
                                disabled={isSyncing}
                                className="filter-sync-btn"
                            >
                                <svg
                                    width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                                    className={isSyncing ? 'sync-icon-rotate' : ''}
                                >
                                    <path d="M21.5 2v6h-6M2.5 22v-6h6M2 12c0-4.4 3.6-8 8-8 3.3 0 6.2 2 7.4 5M22 12c0 4.4-3.6 8-8 8-3.3 0-6.2-2-7.4-5" />
                                </svg>
                                <span>{isSyncing ? 'Syncing...' : 'Sync'}</span>
                            </button>

                            <button 
                                className="filter-create-btn" 
                                onClick={onCreateClick}
                            >
                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                <span>Create Template</span>
                            </button>
                        </div>

                    </div>

                    {/* Templates Grid */}
                    <div className="templates-grid">
                        {sortedTemplates.map(template => (
                            <div className="template-card" key={template.id} style={{ cursor: 'default' }}>

                                <div className="template-card-header">
                                    <div>
                                        <h4 className="template-name">{template.name}</h4>
                                        <div className="template-lang">{template.language}</div>
                                    </div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span className={`status-badge ${getStatusClass(template.status)}`}>
                                            {template.status}
                                        </span>
                                        <TemplateActionMenu
                                            template={template}
                                            onPreview={() => handleOpenPreview(template)}
                                            onArchive={() => handleArchiveTemplate(template)}
                                            onDelete={() => handleDeleteTemplate(template)}
                                        />
                                    </div>
                                </div>

                                <div>
                                    <span className={`category-label ${getCategoryClass(template.category)}`}>
                                        {template.category}
                                    </span>
                                </div>

                                {renderWhatsAppPreview(template)}

                                <div className="template-card-footer">
                                    <span className="last-updated">Updated: {template.updated_at ? new Date(template.updated_at).toLocaleString() : '-'}</span>
                                </div>

                            </div>
                        ))}
                    </div>

                    {/* --- NEW: MODERN PREVIEW MODAL --- */}
                    <TemplatePreviewModal
                        isOpen={isPreviewModalOpen}
                        template={previewTemplate}
                        onClose={handleClosePreview}
                        onSendTemplate={handleStartSending}
                        renderWhatsAppPreview={renderWhatsAppPreview}
                        isFetchingContacts={isFetchingContacts}
                    />

                    {/* --- NEW: MODERN CONTACT SELECTION MODAL --- */}
                    <ContactSelectionModal
                        isOpen={isContactSelectionOpen}
                        contacts={allContacts}
                        selectedContacts={selectedContacts}
                        onSelectContact={toggleContact}
                        onSelectAll={toggleSelectAll}
                        onClose={handleCloseContactSelection}
                        onSend={handleSend}
                        templateName={previewTemplate?.name}
                        isSending={false}
                    />

                    {/* --- NEW: ARCHIVE CONFIRMATION MODAL --- */}
                    <ArchiveConfirmModal
                        isOpen={isArchiveConfirmOpen}
                        templateName={templateToArchive?.name}
                        onConfirm={handleConfirmArchive}
                        onCancel={() => {
                            setIsArchiveConfirmOpen(false);
                            setTemplateToArchive(null);
                        }}
                        isLoading={isProcessing}
                    />

                    {/* --- NEW: DELETE CONFIRMATION MODAL --- */}
                    <DeleteConfirmModal
                        isOpen={isDeleteConfirmOpen}
                        templateName={templateToDelete?.name}
                        onConfirm={handleConfirmDelete}
                        onCancel={() => {
                            setIsDeleteConfirmOpen(false);
                            setTemplateToDelete(null);
                        }}
                        isLoading={isProcessing}
                    />

                    {/* --- REDESIGNED SEND MODAL (Progressive Disclosure) --- */}
                    {isModalOpen && createPortal(
                        <div className="wa-modal-overlay">
                            <div className="wa-modal-container">
                                {showSuccess && (
                                    <div className="success-overlay">
                                        <div className="success-check-icon">✓</div>
                                        <h2 style={{ margin: 0, color: 'var(--text-dark)' }}>Message Sent!</h2>
                                        <p style={{ color: 'var(--text-muted)' }}>Your template has been broadcast successfully.</p>
                                    </div>
                                )}

                                <div className="wa-modal-header">
                                    <h3 className="wa-modal-title">Send {selectedTemplate?.name}</h3>
                                    <button className="close-x" onClick={closeSendModal}>×</button>
                                </div>

                                <div className="wa-modal-content">
                                    {/* Unified Contacts Table */}
                                    <div className="step-section">
                                        <label className="sub-step-label">Select Contacts</label>
                                        <div className="contact-list-box table-wrapper">
                                            <table className="audience-table">
                                                <thead>
                                                    <tr>
                                                        <th className="col-check">
                                                            <div
                                                                className={`custom-checkbox ${selectedContacts.size === ALL_CONTACTS.length && selectedContacts.size > 0 ? 'checked' : ''}`}
                                                                onClick={toggleSelectAll}
                                                                style={{ cursor: 'pointer' }}
                                                            >
                                                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                            </div>
                                                        </th>
                                                        <th className="col-company">Name</th>
                                                        <th className="col-phone">Phone Number</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ALL_CONTACTS.map(contact => (
                                                        <tr
                                                            key={contact.id}
                                                            className={`table-row ${selectedContacts.has(contact.id) ? 'selected' : ''}`}
                                                            onClick={() => toggleContact(contact.id)}
                                                        >
                                                            <td className="col-check">
                                                                <div className={`custom-checkbox ${selectedContacts.has(contact.id) ? 'checked' : ''}`}>
                                                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4"><polyline points="20 6 9 17 4 12"></polyline></svg>
                                                                </div>
                                                            </td>
                                                            <td className="col-company col-primary-text">{contact.name}</td>
                                                            <td className="col-phone col-mono">{contact.phone || contact.phone_number}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>

                                    {/* Step 4: Campaign Name (Only for 2+ contacts) */}
                                    {selectedContacts.size > 1 && (
                                        <div className="step-section campaign-name-section">
                                            <label className="sub-step-label">Campaign Name</label>
                                            <input
                                                type="text"
                                                className="campaign-name-input"
                                                placeholder="e.g., Ugadi Promo Campaign"
                                                value={campaignName}
                                                onChange={(e) => setCampaignName(e.target.value)}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="modal-footer-fixed">
                                    <button className="btn-secondary" onClick={closeSendModal}>Cancel</button>
                                    <button
                                        className="btn-send-main"
                                        disabled={selectedContacts.size === 0 || (selectedContacts.size > 1 && !campaignName.trim())}
                                        onClick={handleSend}
                                    >
                                        {selectedContacts.size > 1
                                            ? `Launch Campaign (${selectedContacts.size} contacts)`
                                            : `Send to ${selectedContacts.size} contact`
                                        }
                                    </button>
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}

                    {/* --- EDIT MODAL --- */}
                    {isEditModalOpen && editingTemplate && createPortal(
                        <div className="wa-modal-overlay">
                            <div className="wa-modal-container" style={{ maxWidth: '600px' }}>
                                <div className="wa-modal-header">
                                    <h3 className="wa-modal-title">Edit Template</h3>
                                    <button className="close-x" onClick={closeEditModal}>×</button>
                                </div>

                                <div className="wa-modal-content" style={{ padding: '24px' }}>
                                    {/* Template Details */}
                                    <div style={{ marginBottom: '24px' }}>
                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-gray)', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>
                                                Template Name
                                            </label>
                                            <p style={{ margin: 0, fontSize: '16px', color: 'var(--text-dark)', fontWeight: '500' }}>
                                                {editingTemplate.name}
                                            </p>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-gray)', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>
                                                Category
                                            </label>
                                            <span className={`category-label ${getCategoryClass(editingTemplate.category)}`}>
                                                {editingTemplate.category}
                                            </span>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-gray)', textTransform: 'uppercase', marginBottom: '8px', display: 'block' }}>
                                                Status
                                            </label>
                                            <span className={`status-badge ${getStatusClass(editingTemplate.status)}`}>
                                                {editingTemplate.status}
                                            </span>
                                        </div>

                                        <div style={{ marginBottom: '16px' }}>
                                            <label style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-gray)', textTransform: 'uppercase', marginBottom: '12px', display: 'block' }}>
                                                Live Preview
                                            </label>
                                            {renderWhatsAppPreview(editingTemplate)}
                                        </div>
                                    </div>
                                </div>

                                <div className="modal-footer-fixed" style={{ display: 'flex', gap: '12px' }}>
                                    <button className="btn-secondary" onClick={closeEditModal} disabled={isApprovingTemplate}>
                                        Close
                                    </button>
                                    {editingTemplate.status === 'PENDING' && (
                                        <>
                                            <button
                                                className="btn-icon"
                                                onClick={handleRejectTemplate}
                                                disabled={isApprovingTemplate}
                                                style={{
                                                    padding: '10px 16px',
                                                    background: '#ff6b6b',
                                                    color: 'white',
                                                    border: 'none',
                                                    borderRadius: '8px',
                                                    cursor: isApprovingTemplate ? 'not-allowed' : 'pointer',
                                                    opacity: isApprovingTemplate ? 0.6 : 1,
                                                    fontSize: '14px',
                                                    fontWeight: '600'
                                                }}
                                            >
                                                {isApprovingTemplate ? 'Processing...' : 'Reject'}
                                            </button>
                                            <button
                                                className="btn-send-main"
                                                onClick={handleApproveTemplate}
                                                disabled={isApprovingTemplate}
                                                style={{
                                                    background: isApprovingTemplate ? '#ccc' : 'linear-gradient(135deg, #0084ff 0%, #0066cc 100%)',
                                                    cursor: isApprovingTemplate ? 'not-allowed' : 'pointer'
                                                }}
                                            >
                                                {isApprovingTemplate ? 'Approving...' : 'Approve Template'}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>,
                        document.body
                    )}
                </>
            )}
        </main>
    );
};

export default TemplatesView;



